import moment from 'momentExt'

/** Inclusive local days. Same contract as papaya `INVENTORY_MAX_RANGE_DAYS`. */
export const INVENTORY_MAX_RANGE_DAYS = 30

/**
 * Earliest calendar date the Inventory picker may select (today minus this many days).
 * 31 → 32 inclusive dates, matching ingest's 32 UTC-day snapshot retain buffer.
 */
export const INVENTORY_CALENDAR_LOOKBACK_DAYS = 31

export function inventoryEarliestSelectableDate(now = moment()) {
  return moment(now).startOf('day').subtract(INVENTORY_CALENDAR_LOOKBACK_DAYS, 'days')
}

/**
 * Clamp a picker range so it fits snapshot retain: span ≤ 30 local days, and both
 * ends on or after today-31. Longer presets keep `end` and slide `start` forward
 * (same as papaya), then clip to the lookback floor.
 */
export function clampInventoryPickerRange(
  fromDate?: string | Date | null,
  toDate?: string | Date | null,
  now = moment(),
): { start: string; end: string } {
  const today = moment(now).startOf('day')
  const earliest = inventoryEarliestSelectableDate(today)
  let end = toDate ? moment(toDate).startOf('day') : today.clone()
  let start = fromDate
    ? moment(fromDate).startOf('day')
    : end.clone().subtract(INVENTORY_MAX_RANGE_DAYS - 1, 'days')

  if (!end.isValid()) end = today.clone()
  if (!start.isValid()) {
    start = end.clone().subtract(INVENTORY_MAX_RANGE_DAYS - 1, 'days')
  }

  if (end.isAfter(today)) end = today.clone()
  if (end.isBefore(earliest)) end = earliest.clone()
  if (start.isBefore(earliest)) start = earliest.clone()
  if (start.isAfter(end)) start = end.clone()

  const inclusive = end.diff(start, 'days') + 1
  if (inclusive > INVENTORY_MAX_RANGE_DAYS) {
    start = end.clone().subtract(INVENTORY_MAX_RANGE_DAYS - 1, 'days')
    if (start.isBefore(earliest)) start = earliest.clone()
  }

  return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') }
}

export function filterInventoryQuickRanges(
  quickDateRanges: Record<string, any>,
  now = moment(),
): Record<string, any> {
  const earliest = inventoryEarliestSelectableDate(now)
  const today = moment(now).startOf('day')
  const out: Record<string, any> = {}
  Object.keys(quickDateRanges || {}).forEach((label) => {
    const value = quickDateRanges[label]
    if (!value || value.divider || !value.start || !value.end) return
    const start = moment(value.start).startOf('day')
    const end = moment(value.end).startOf('day')
    if (!start.isValid() || !end.isValid()) return
    if (start.isBefore(earliest, 'day')) return
    if (end.isAfter(today, 'day')) return
    const inclusive = end.diff(start, 'days') + 1
    if (inclusive > INVENTORY_MAX_RANGE_DAYS) return
    out[label] = value
  })
  return out
}
